import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Article as UIArticle } from '@shopee/chatbot-ui';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';
import { ArticleProps as UIArticleProps, ArticleStatus, FAQ } from '@shopee/chatbot-ui/lib/components/Article';

import { afterDialogueShow, getTrans, handleClickHistoryButton } from '@/utils/tools';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { HISTORY_TRANSIFY_KEY, TRACKING_MSG_TYPE } from '@/utils/constants';
import API from '@/services';
import { ChatbotUIBasicProps } from '@/types/common';
import { updateDialogueInfo, updateDialogueOuterData } from '@/utils/sessionUtils';
import { isSeparate, isScrollToUnread } from '@/utils/experiment/getArticleUIUXExperimen';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import handleClickIntent from '@/utils/intent/handleClickIntent';
import { trackingHandler } from '@/utils/btnClickHandler';
import { toJS } from 'mobx';
import { IntentType } from '@/types/intent';
import { sessionStore } from '@/stores';
import { featureToggle, FEATURE_TOGGLE_TYPE } from '@/utils/featureToggleConfig';
import { isBank } from '@/config/core';

export type ArticleProps = UIArticleProps & {
  article_number: string;
  kb_intent_id: string;
  kb_bot_id: string;
  kb_edition: string;
  kb_answer_id: string;
  agentStore?: any;
  sessionStore?: any;
  listPos?: number;
  useUIOptimisationRecommender?: boolean;
  faq?: any;
  shouldDisplayInstantly?: boolean;
  isSingleArticle?: boolean;
  needOpen?: boolean;
} & ChatbotUIBasicProps;

const Article = (props: ArticleProps) => {
  const faqObject = toJS(props?.faq);

  const [articleStatus, setArticleStatus] = useState(props.content ? ArticleStatus.FINISH : ArticleStatus.LOADING);
  const [content, setContent] = useState(props.content || '');
  const [exceededMaxHeight, setExceededMaxHeight] = useState(false);

  const loadArticleContent = async () => {
    setArticleStatus(ArticleStatus.LOADING);

    const { kb_intent_id, kb_bot_id, kb_edition, kb_answer_id, article_number, isSingleArticle } = props;

    let params = {};
    if (isSingleArticle && article_number != null && article_number.length !== 0) {
      params = { article_num: article_number, kb_edition };
    } else {
      params = { kb_intent_id, kb_bot_id, kb_edition, kb_answer_id };
    }

    const data = await (API as { getArticleContent: Function }).getArticleContent(params).catch(() => {});

    if (!data) {
      setArticleStatus(ArticleStatus.ERROR);
    } else if (data.hasDeleted) {
      setArticleStatus(ArticleStatus.DELETED);
    } else {
      setContent(data.content);
      setArticleStatus(ArticleStatus.FINISH);
    }
  };
  useEffect(() => {
    if (!props.content) {
      loadArticleContent();
    }
  }, []);

  const trackingProps = {
    componentTrackingData: {
      article: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.ARTICLE],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ARTICLE],
        trackingdataBasic: {
          article_id: props.article_number,
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
      },
      reload: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.RELOAD_ARTICLE],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.RELOAD_ARTICLE],
        trackingdataBasic: {
          current_chat_session_id: props.sessionId || '',
          article_id: props.article_number,
        },
      },
      delete: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.DELETED_ARTICLE],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.DELETED_ARTICLE],
        trackingdataBasic: {
          current_chat_session_id: props.sessionId || '',
          article_id: props.article_number,
        },
      },
      faq: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.KEY_ANSWER_FAQ_IN_DETAIL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.KEY_ANSWER_FAQ_IN_DETAIL],
        trackingdataBasic: {
          article_id: props.article_number,
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
        trackingItemData: (data: FAQ & { location: number }) => ({
          text: data?.text,
          location: data?.location,
          content: data?.hotquestion_text,
          intent_source: data?.intent_source,
          intent_id: data?.intent_id,
          is_ds_predicted: data?.intent_type === IntentType.Prediction,
        }),
      },
    },
  };

  const articleContentTrackingData = {
    chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
    chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
    intent_id: Number(props.kb_intent_id),
    bot_id: Number(props.kb_bot_id),
    article_id: props.article_number,
    article_title: props.title,
  };

  const onClickFaq = (faq: FAQ, option?: { trackingData?: any; isInDetail?: boolean }) => {
    const { agentStore } = props;

    const { trackingData, isInDetail } = option || {};

    // 在agent过程中，走agent逻辑
    if (agentStore.inAgent) {
      sendBotMessageToAgent({ content: faq.text });
    } else {
      // 走ChatBot逻辑
      const params = { ...(faq?.query_callback || {}) };
      const newParams: any = { ...params };
      handleClickIntent(faq.text, newParams, (res: any) => {
        // 因为需要在点击时添加 next_chat_dialogue_id 这个 id 需要请求后才获得，所以把相应参数从 chat ui 传回这里，然后触发点击上报
        if (trackingData) {
          trackingHandler({
            trackingname: TRACKING_TYPE.KEY_ANSWER_FAQ_IN_DETAIL,
            trackingdata: { ...trackingData, next_chat_dialogue_id: res?.dialogue_id },
          });
        }
      });
    }
  };

  const onClickDisabledFaq = () => {
    handleClickHistoryButton();
  };

  const onExceededMaxHeightChange = (exceeded: boolean) => {
    setExceededMaxHeight(exceeded);

    if (!exceeded && !props.isHistory && !isSeparate() && props.listPos) {
      setTimeout(() => {
        const dialoueList = sessionStore.dialogueList;
        const nextDialogue = dialoueList[props.listPos + 1];

        if (nextDialogue?.hidden) {
          updateDialogueOuterData({ hidden: false }, props.listPos + 1);
          afterDialogueShow();
        }
      }, 1000);
    }
  };

  useEffect(() => {
    if (!featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE) && props.shouldDisplayInstantly) {
      props.listPos && updateDialogueInfo({ shouldDisplayInstantly: false }, props.listPos);
    }
  }, [props.listPos, props.shouldDisplayInstantly]);

  useEffect(() => {
    if (featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE) && props.needOpen && exceededMaxHeight) {
      props.listPos && updateDialogueInfo({ needOpen: false }, props.listPos);
    }
  }, [props.listPos, props.needOpen, exceededMaxHeight]);

  return (
    <UIArticle
      {...(props as any)}
      {...trackingProps}
      content={content}
      status={articleStatus}
      disabledFaq={props.isHistory}
      onClickDisabledFaq={onClickDisabledFaq}
      faqTitle={faqObject?.faqTitle}
      faqIntentList={faqObject?.faqIntentList?.map?.((faq: any) => ({
        ...faq,
        text: faq.hotquestion_text || faq.intent_name,
      }))}
      onClickFaq={onClickFaq}
      autoPopup={!featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE) && !props.isHistory && props.shouldDisplayInstantly && !props.agentStore.inAgent}
      needPopup={!props.isHistory && props.needOpen && !props.agentStore.inAgent}
      isSeparate={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE) && isSeparate() && exceededMaxHeight}
      isScrollToUnread={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE) && isScrollToUnread()}
      isOldUI={!featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE)}
      viewMoreText={getTrans('key_answer_view_more_btn')}
      unreadText={getTrans('unread_text')}
      summaryMaxHeight={412}
      reloadArticle={loadArticleContent}
      reloadText={getTrans(HISTORY_TRANSIFY_KEY.RELOAD)}
      failToLoadText={getTrans(HISTORY_TRANSIFY_KEY.FAIL_TO_LOAD_FILE)}
      articleDeletedText={getTrans(HISTORY_TRANSIFY_KEY.ARTICLE_HAS_BEEN_DELETED)}
      articleContentTrackingData={articleContentTrackingData}
      disableSpecialLink={props.isHistory}
      useUIOptimisation={props?.useUIOptimisationRecommender}
      onExceededMaxHeightChange={onExceededMaxHeightChange}
      useUIOptimisationRecommender={props?.useUIOptimisationRecommender}
      showExpandIcon={true}
      isUIOptimisationAccordion={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_ACCORDION)}
      isUIOptimisationButton={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON)}
      isUIOptimisationTable={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE)}
      popupClassName={isBank() ? 'chatbot-bank-article-modal' : ''}
    />
  );
};

export default inject(getChildInject('agentStore', 'sessionStore'))(observer(Article));
